import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Table, Alert, Spinner, Badge, Card, ButtonGroup } from 'react-bootstrap';
import { wasteReportAPI } from '../api/wasteReportAPI';
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI';
import { usePermissions } from '../contexts/PermissionContext';
import SearchInputWithClear from '../components/SearchInputWithClear';
import { getWasteColor, getWasteBadgeVariant } from '../utils/wasteUtils';

const daysOptions = [
    { value: 0, label: 'All Time' },
    { value: 30, label: 'Last 30 Days' },
    { value: 60, label: 'Last 60 Days' },
    { value: 90, label: 'Last 90 Days' },
    { value: 180, label: 'Last 180 Days' },
    { value: 365, label: 'Last 365 Days' },
];

function WastePercentageReportScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reportData, setReportData] = useState(null);

    const [selectedDays, setSelectedDays] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInputValue, setSearchInputValue] = useState('');

    const [availableCategories, setAvailableCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);

    const [expandedRecipeIds, setExpandedRecipeIds] = useState(new Set());
    const [activeView, setActiveView] = useState('categories');

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categories = await stockItemCategoryAPI.getAll();
                setAvailableCategories(categories || []);
                const allIds = (categories || []).map(cat => cat._id);
                setSelectedCategories(allIds);
                setCategoriesLoaded(true);
            } catch (err) {
                console.error('Error fetching categories:', err);
                setCategoriesLoaded(true);
            }
        };
        fetchCategories();
    }, []);

    const fetchReport = useCallback(async () => {
        if (!categoriesLoaded) return;
        setLoading(true);
        setError('');
        try {
            const allSelected = selectedCategories.length === availableCategories.length;
            const categoriesParam = allSelected ? '' : selectedCategories.join(',');
            const data = await wasteReportAPI.getReport(selectedDays, categoriesParam, searchTerm);
            setReportData(data);
        } catch (err) {
            console.error('Error fetching waste report:', err);
            setError(err.response?.data?.error || 'Failed to load waste percentage report');
        } finally {
            setLoading(false);
        }
    }, [selectedDays, selectedCategories, searchTerm, categoriesLoaded]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchTerm(searchInputValue);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInputValue]);

    const toggleCategory = (categoryId) => {
        setSelectedCategories(prev => {
            const numId = Number(categoryId);
            return prev.includes(numId)
                ? prev.filter(id => id !== numId)
                : [...prev, numId];
        });
    };

    const toggleAllCategories = () => {
        if (selectedCategories.length === availableCategories.length) {
            setSelectedCategories([]);
        } else {
            setSelectedCategories(availableCategories.map(cat => cat._id));
        }
    };

    const toggleRecipeExpand = (recipeId) => {
        setExpandedRecipeIds(prev => {
            const next = new Set(prev);
            if (next.has(recipeId)) {
                next.delete(recipeId);
            } else {
                next.add(recipeId);
            }
            return next;
        });
    };

    if (permissionsLoading) {
        return (
            <Container className="py-4 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (!hasPermission('reports.generate')) {
        return (
            <Container className="py-4">
                <Alert variant="danger">You do not have permission to view reports.</Alert>
            </Container>
        );
    }

    const summary = reportData?.summary;
    const perRecipe = reportData?.per_recipe || [];
    const perCategory = reportData?.per_category || [];

    return (
        <Container fluid className="py-3">
            <h4 className="mb-3">Waste Percentage Report</h4>
            <Row>
                {/* Main content */}
                <Col lg={9}>
                    {/* Summary cards */}
                    {summary && !loading && (
                        <Row className="mb-3 g-3">
                            <Col sm={4}>
                                <Card className="text-center h-100">
                                    <Card.Body className="py-2">
                                        <div className="text-muted" style={{ fontSize: '13px' }}>Recipes Analyzed</div>
                                        <div className="fs-4 fw-bold text-primary">{summary.total_recipes}</div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col sm={4}>
                                <Card className="text-center h-100">
                                    <Card.Body className="py-2">
                                        <div className="text-muted" style={{ fontSize: '13px' }}>Production Lots</div>
                                        <div className="fs-4 fw-bold text-success">{summary.total_lots}</div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col sm={4}>
                                <Card className="text-center h-100">
                                    <Card.Body className="py-2">
                                        <div className="text-muted" style={{ fontSize: '13px' }}>Overall Avg Waste</div>
                                        <div className={`fs-4 fw-bold ${getWasteColor(summary.overall_avg_waste_pct)}`}>
                                            {summary.overall_avg_waste_pct}%
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* View toggle */}
                    <div className="mb-3">
                        <ButtonGroup size="sm">
                            <Button
                                variant={activeView === 'recipes' ? 'primary' : 'outline-primary'}
                                onClick={() => setActiveView('recipes')}
                            >
                                By Recipe
                            </Button>
                            <Button
                                variant={activeView === 'categories' ? 'primary' : 'outline-primary'}
                                onClick={() => setActiveView('categories')}
                            >
                                By Category
                            </Button>
                        </ButtonGroup>
                    </div>

                    {/* Content area */}
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="text-muted mt-2">Generating report...</p>
                        </div>
                    ) : error ? (
                        <Alert variant="danger">{error}</Alert>
                    ) : activeView === 'recipes' ? (
                        /* By Recipe table */
                        perRecipe.length > 0 ? (
                            <Table responsive hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '30px' }}></th>
                                        <th>ID</th>
                                        <th>Recipe</th>
                                        <th>Item #</th>
                                        <th className="text-center">Lots</th>
                                        <th className="text-center">Avg Waste %</th>
                                        <th className="text-center">Min</th>
                                        <th className="text-center">Max</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {perRecipe.map(recipe => (
                                        <React.Fragment key={recipe.recipe_id}>
                                            <tr
                                                onClick={() => toggleRecipeExpand(recipe.recipe_id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td>
                                                    <i className={`bi bi-chevron-${expandedRecipeIds.has(recipe.recipe_id) ? 'down' : 'right'}`}></i>
                                                </td>
                                                <td>{recipe.recipe_id}</td>
                                                <td>
                                                    <strong>{recipe.recipe_name}</strong>
                                                </td>
                                                <td>{recipe.item_number}</td>
                                                <td className="text-center">{recipe.lots_analyzed}</td>
                                                <td className="text-center">
                                                    <Badge bg={getWasteBadgeVariant(recipe.avg_waste_pct)}>
                                                        {recipe.avg_waste_pct}%
                                                    </Badge>
                                                </td>
                                                <td className={`text-center ${getWasteColor(recipe.min_waste_pct)}`}>
                                                    {recipe.min_waste_pct}%
                                                </td>
                                                <td className={`text-center ${getWasteColor(recipe.max_waste_pct)}`}>
                                                    {recipe.max_waste_pct}%
                                                </td>
                                            </tr>
                                            {expandedRecipeIds.has(recipe.recipe_id) && recipe.ingredients.length > 0 && (
                                                <tr>
                                                    <td colSpan={8} className="p-0">
                                                        <Table size="sm" className="mb-0" style={{ backgroundColor: 'var(--bs-tertiary-bg)' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ paddingLeft: '40px' }}>Ingredient</th>
                                                                    <th className="text-center">Current %</th>
                                                                    <th className="text-center">Avg Realized %</th>
                                                                    <th className="text-center">Min</th>
                                                                    <th className="text-center">Max</th>
                                                                    <th className="text-center">Lots</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {recipe.ingredients.map((ing, idx) => (
                                                                    <tr key={idx}>
                                                                        <td style={{ paddingLeft: '40px' }}>{ing.ingredient_name}</td>
                                                                        <td className="text-center">{ing.current_waste_pct}%</td>
                                                                        <td className="text-center">
                                                                            <span className={getWasteColor(ing.avg_waste_pct)}>
                                                                                {ing.avg_waste_pct}%
                                                                            </span>
                                                                        </td>
                                                                        <td className={`text-center ${getWasteColor(ing.min_waste_pct)}`}>
                                                                            {ing.min_waste_pct}%
                                                                        </td>
                                                                        <td className={`text-center ${getWasteColor(ing.max_waste_pct)}`}>
                                                                            {ing.max_waste_pct}%
                                                                        </td>
                                                                        <td className="text-center">{ing.lots_analyzed}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <Alert variant="info">
                                No completed production lots found for the selected period and categories.
                            </Alert>
                        )
                    ) : (
                        /* By Category table */
                        perCategory.length > 0 ? (
                            <Table responsive hover size="sm">
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th className="text-center">Ingredients</th>
                                        <th className="text-center">Lots</th>
                                        <th className="text-center">Avg Waste %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {perCategory.map(cat => (
                                        <tr key={cat.category_id}>
                                            <td><strong>{cat.category_name}</strong></td>
                                            <td className="text-center">{cat.ingredients_count}</td>
                                            <td className="text-center">{cat.lots_count}</td>
                                            <td className="text-center">
                                                <Badge bg={getWasteBadgeVariant(cat.avg_waste_pct)}>
                                                    {cat.avg_waste_pct}%
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <Alert variant="info">
                                No category data available for the selected period and categories.
                            </Alert>
                        )
                    )}
                </Col>

                {/* Sidebar filters */}
                <Col lg={3}>
                    <Card className="mb-3">
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold" style={{ fontSize: '14px' }}>Time Period</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={selectedDays}
                                    onChange={(e) => setSelectedDays(Number(e.target.value))}
                                >
                                    {daysOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold" style={{ fontSize: '14px' }}>Search Recipe / GTIN</Form.Label>
                                <SearchInputWithClear
                                    value={searchInputValue}
                                    onChange={(e) => setSearchInputValue(e.target.value)}
                                    onClear={() => { setSearchInputValue(''); setSearchTerm(''); }}
                                    placeholder="Recipe name or GTIN..."
                                    size="sm"
                                />
                            </Form.Group>

                            <div>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span className="fw-bold" style={{ fontSize: '14px' }}>Categories</span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={toggleAllCategories}
                                        style={{ fontSize: '11px', padding: '2px 8px' }}
                                    >
                                        {selectedCategories.length === availableCategories.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </div>
                                <div
                                    className="d-flex flex-wrap gap-2 p-2 border rounded"
                                    style={{
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                        backgroundColor: 'var(--bs-body-bg)'
                                    }}
                                >
                                    {availableCategories.map(category => (
                                        <Badge
                                            key={category._id}
                                            bg={selectedCategories.includes(category._id) ? 'success' : 'light'}
                                            text={selectedCategories.includes(category._id) ? 'white' : 'dark'}
                                            style={{ cursor: 'pointer', fontSize: '12px' }}
                                            onClick={() => toggleCategory(category._id)}
                                        >
                                            {category.name}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default WastePercentageReportScreen;
